import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import Contact from '../../home/components/Contact';
import { industryServicesData } from '../../../mocks/industryServices';
import { testimonials } from '../../../mocks/testimonials';
import { useSEO, getLocalBusinessSchema } from '../../../utils/seo';

export default function NewYorkPage() {
  useSEO({
    title: 'New York Contractor Digital Marketing | NYC Contractor Marketing Agency',
    description: 'Leading digital marketing agency for contractors in New York City and the tri-state area. Generate high-quality leads with specialized SEO, Google Ads, web design, and social media marketing for NYC contractors.',
    keywords: 'New York contractor marketing, NYC contractor digital marketing, New York contractor SEO, NYC contractor advertising, Manhattan contractor marketing',
    canonical: '/locations/new-york',
    schema: getLocalBusinessSchema('New York', 'NY')
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to the contact section – defensive in case the element is missing
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const allServices = [
    'Web Design & Development',
    'AI Integration & Automation',
    'Search Engine Optimization (SEO)',
    'Social Media Marketing',
    'Google Local Services Ads (LSA)',
    'Pay-Per-Click Advertising (PPC)',
    'Reputation Management',
    'Content Marketing',
    'Email Marketing',
    'Lead Generation',
  ];

  const industries = [
    { name: 'Electrical Contractors', icon: 'ri-flashlight-line' },
    { name: 'HVAC Services', icon: 'ri-temp-cold-line' },
    { name: 'Landscaping Companies', icon: 'ri-plant-line' },
    { name: 'Pest Control Services', icon: 'ri-bug-line' },
    { name: 'Plumbing Contractors', icon: 'ri-drop-line' },
    { name: 'Roofing Companies', icon: 'ri-home-4-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              New York Contractor Digital Marketing Agency
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Dominate the New York market with specialized digital marketing strategies designed exclusively for contractors. Generate high-quality leads and grow your contracting business in the most competitive market in America.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={scrollToContact}
                className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Your Free Marketing Audit <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a
                href="tel:2153963311"
                className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i> (215) 396-3311
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About New York Market */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Digital Marketing for New York Contractors
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                New York City and the surrounding metro area represent one of the most lucrative markets for contractors in the United
                States. With over 8 million residents in NYC alone and millions more in the tri‑state area, the demand for quality
                contracting services is constant and growing.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                However, the New York market is also incredibly competitive. To succeed, contractors need more than just great work— they
                need a powerful digital marketing strategy that puts them in front of customers at the exact moment they're searching for
                services.
              </p>
              <div className="space-y-4">
                {[
                  'Rank #1 on Google for high-value New York contractor searches',
                  'Generate qualified leads from Manhattan, Brooklyn, Queens, Bronx, and Staten Island',
                  'Build a dominant online presence in the competitive NYC market',
                  'Attract commercial and residential clients with proven marketing strategies',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/ecb574abd4281d7076129a125aa907be.jpg"
                alt="New York City Skyline"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Company */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
              Why New York Contractors Choose Million Dollar Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize exclusively in contractor marketing and understand the unique challenges of growing a contracting business in
              New York's competitive landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-trophy-line',
                title: 'Proven Track Record',
                description:
                  "We've helped dozens of New York contractors dominate their local markets and achieve consistent 6-figure and 7-figure revenues through strategic digital marketing.",
              },
              {
                icon: 'ri-team-line',
                title: 'Contractor‑Focused Expertise',
                description:
                  'Unlike general marketing agencies, we only work with contractors. We understand your business model, customer journey, and what it takes to generate high‑quality leads.',
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Results‑Driven Approach',
                description:
                  'Our strategies are built on data and results. We focus on metrics that matter: qualified leads, booked appointments, and revenue growth for your contracting business.',
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Local Market Mastery',
                description:
                  'We know the New York market inside and out. From Manhattan to Long Island, we understand local search patterns and how to capture customers in your service area.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Dedicated Support',
                description:
                  "You'll work directly with experienced marketing strategists who understand your business and are committed to your success in the New York market.",
              },
              {
                icon: 'ri-rocket-line',
                title: 'Cutting‑Edge Technology',
                description:
                  'We leverage the latest AI tools, automation, and marketing technology to give you a competitive advantage and maximize your marketing ROI.',
              },
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-black mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              New York Contractor Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized digital marketing strategies for every type of contractor in the New York metro area
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600 group hover:bg-red-600 cursor-pointer"
              >
                <div className="w-14 h-14 bg-red-600 group-hover:bg-white rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <i className={`${industry.icon} text-2xl text-white group-hover:text-red-600 transition-colors`}></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-black group-hover:text-white mb-2 transition-colors">
                  {industry.name}
                </h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors">
                  Dominate New York searches for {industry.name.toLowerCase()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Complete Digital Marketing Services for New York Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to dominate the New York contractor market and generate consistent, high-quality leads
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allServices.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-black">{service}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Success Stories from New York Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped contractors across New York grow their businesses with proven digital marketing strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 flex flex-col"
              >
                {/* Star Rating */}
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-red-500 text-xl"></i>
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-white text-base leading-relaxed mb-8 flex-grow">
                  "{testimonial.quote}"
                </p>

                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={`https://readdy.ai/api/search-image?query=professional%20contractor%20headshot%20portrait%20of%20confident%20smiling%20$%7Btestimonial.trade%7D%20worker%20wearing%20work%20uniform%20or%20company%20polo%20shirt%20realistic%20person%20face%20close%20up%20detailed%20facial%20features%20natural%20lighting%20high%20quality%20commercial%20business%20photography%20clean%20simple%20neutral%20background%20professional%20studio%20lighting%20friendly%20approachable%20expression&width=96&height=96&seq=testimonial-avatar-${testimonial.id}&orientation=squarish`}
                      alt={testimonial.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-base">{testimonial.name}</p>
                    <p className="text-gray-400 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strong CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Dominate the New York Contractor Market?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your free marketing audit and discover exactly how to generate more leads, book more jobs, and grow your contracting
            business in New York.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Your Free Audit Now <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <a
              href="tel:2153963311"
              className="inline-flex items-center px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i> Call (215) 396-3311
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <div id="contact">
        <Contact />
      </div>

      <Footer />
    </div>
  );
}
